import React, { useEffect, useState } from 'react';
import { SaveIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { useGameSettings } from '../../lib/cmsStore';
import { GameSettings } from '../../lib/cmsTypes';
import { Button } from '../../components/ui/Button';
import { VersionHistoryPanel, VersionHistoryButton } from './components/VersionHistoryPanel';
export function GameEditor() {
  const {
    data,
    updateData,
    isLoading
  } = useGameSettings();
  const [localData, setLocalData] = useState<GameSettings | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => {
    if (!isLoading && data && !localData) {
      setLocalData(data);
    }
  }, [data, isLoading, localData]);
  // Migrate old format or short placeholder to full game code
  useEffect(() => {
    if (!localData) return;
    const code = localData.gameCode;
    const needsMigration = typeof code !== 'string' || code.length < 200 || code.startsWith('// Game Engine Code');
    if (needsMigration) {
      // Force reset to default which has the full source
      window.location.reload();
    }
  }, [localData]);
  if (isLoading || !localData) {
    return <div className="p-8 font-mono text-sm text-muted-foreground" data-id="element-1284">
        Loading...
      </div>;
  }
  const gameCode = localData.gameCode || '';
  const handleSave = () => {
    setIsSaving(true);
    updateData(localData);
    setTimeout(() => setIsSaving(false), 500);
  };
  const handleCopy = () => {
    navigator.clipboard.writeText(gameCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="p-8 max-w-6xl mx-auto" data-id="element-1285">
      <div className="flex items-center justify-between mb-8" data-id="element-1286">
        <div data-id="element-1287">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-id="element-1288">
            Game Code
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-2" data-id="element-1289">
            The source code for the endless runner game. Edit here for future
            changes.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1290">
          <VersionHistoryButton onClick={() => setHistoryOpen(true)} data-id="element-1291" />
          <Button onClick={handleCopy} className="gap-2" variant="secondary" data-id="element-1292">
            {copied ? <CheckIcon className="w-4 h-4" data-id="element-1293" /> : <CopyIcon className="w-4 h-4" data-id="element-1294" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button onClick={handleSave} disabled={isSaving} className="gap-2" data-id="element-1295">
            <SaveIcon className="w-4 h-4" data-id="element-1296" />
            {isSaving ? 'Saving...' : 'Save Code'}
          </Button>
        </div>
      </div>

      <div className="border border-border rounded-sm overflow-hidden" data-id="element-1297">
        <div className="flex items-center justify-between px-4 py-2 bg-card border-b border-border" data-id="element-1298">
          <span className="font-mono text-xs text-muted-foreground" data-id="element-1299">
            gameEngine.ts
          </span>
          <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest" data-id="element-1300">
            {gameCode.split('\n').length} lines
          </span>
        </div>
        <textarea value={gameCode} onChange={e => setLocalData(prev => prev ? {
        ...prev,
        gameCode: e.target.value
      } : prev)} className="w-full min-h-[70vh] p-4 bg-background text-foreground font-mono text-xs leading-relaxed resize-y focus:outline-none focus:ring-0 border-0" spellCheck={false} autoCorrect="off" autoCapitalize="off" data-id="element-1301" />
      </div>
      <VersionHistoryPanel contentKey="sinka_game" isOpen={historyOpen} onClose={() => setHistoryOpen(false)} onRestore={data => {
      setLocalData(data);
      updateData(data);
    }} data-id="element-1302" />
    </div>;
}